import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, CloudOff, CloudUpload, Mail, Menu, X } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { useConnection } from '@/hooks/useConnection';
import { LoadingState } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { LEARNER_GROUPS, type NavGroup } from './navigation';
import { Logo } from './Logo';
import { UserMenu } from './UserMenu';

/**
 * Coque applicative. Sans enfant, elle rend la route imbriquee ; avec un
 * enfant, elle l'encadre directement — ce qui permet de servir une page
 * publique (le centre d'aide) dans la meme mise en page une fois connecte.
 */
export function AppShell({ children }: { children?: ReactNode }) {

  const { t } = useLanguage();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Le menu mobile se referme a chaque navigation.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const { data: notifications } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.get<{ unread: number }>('/notifications', { query: { pageSize: 1 } }),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });

  const { data: messages } = useQuery({
    queryKey: ['messages', 'unread'],
    queryFn: () => api.get<{ unread: number }>('/messages', { query: { pageSize: 1 } }),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });

  const unread = notifications?.unread ?? 0;
  const unreadMessages = messages?.unread ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-ink"
      >
        {t('a11y.skipToContent')}
      </a>

      {/* Barre laterale — navigation principale */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-border bg-surface transition-transform duration-300 lg:translate-x-0',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <Logo size={38} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-base font-bold leading-tight text-ink">{t('app.name')}</p>
            <p className="truncate text-[11px] uppercase tracking-wide text-ink-subtle">{t('app.institution')}</p>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label={t('nav.closeMenu')}
            className="rounded-lg p-1.5 text-ink-subtle hover:bg-surface-muted lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5">
          {LEARNER_GROUPS.map((group) => (
            <NavSection key={group.titleKey} group={group} />
          ))}
        </nav>

        <div className="space-y-3 border-t border-border px-4 py-4">
          {/* Ce qui appelle une reaction immediate reste a portee de clic. */}
          <div className="flex items-center gap-2">
            <ShortcutLink to="/notifications" label={t('nav.notifications')} count={unread} icon={Bell} />
            <ShortcutLink to="/messages" label={t('nav.messages')} count={unreadMessages} icon={Mail} />
          </div>

          <UserMenu />
        </div>

      </aside>

      {menuOpen ? (
        <div className="fixed inset-0 z-30 bg-ink/40 backdrop-blur-sm lg:hidden" onClick={() => setMenuOpen(false)} aria-hidden />
      ) : null}

      <div className="lg:pl-72">
        {/* Barre superieure — mobile */}
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-surface/90 px-4 py-3 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={t('nav.openMenu')}
            className="rounded-lg p-2 text-ink transition-colors hover:bg-surface-muted"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/dashboard" className="flex min-w-0 flex-1 items-center gap-2">
            <Logo size={28} />
            <span className="truncate font-display text-sm font-bold text-ink">{t('app.name')}</span>
          </Link>
          <MobileShortcut to="/messages" label={t('nav.messages')} count={unreadMessages} icon={Mail} />
          <MobileShortcut to="/notifications" label={t('nav.notifications')} count={unread} icon={Bell} />
        </header>

        <ConnectionBanner />

        <main id="main-content" className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          {/* Les pages chargees a la demande s'affichent sans faire disparaitre
              la navigation. | Lazy pages load without hiding the navigation. */}
          <Suspense fallback={<LoadingState />}>{children ?? <Outlet />}</Suspense>
        </main>

        <footer className="mx-auto w-full max-w-7xl px-4 pb-8 text-center text-xs text-ink-subtle sm:px-6 lg:px-8">
          <p>
            {t('app.country')} — {t('app.institution')} · {t('app.motto')}
          </p>
        </footer>
      </div>
    </div>
  );
}

function NavSection({ group }: { group: NavGroup }) {
  const { t } = useLanguage();

  return (
    <div>
      <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
        {t(group.titleKey)}
      </p>
      <ul className="space-y-0.5">
        {group.items.map(({ to, labelKey, icon: Icon, end }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-soft text-primary'
                    : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                )
              }
            >
              <Icon className="h-4.5 w-4.5 shrink-0" aria-hidden />
              <span className="flex-1 truncate">{t(labelKey)}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Raccourci en pied de barre, avec le nombre d'elements non lus. */
function ShortcutLink({
  to,
  label,
  count,
  icon: Icon,
}: {
  to: string;
  label: string;
  count: number;
  icon: typeof Bell;
}) {
  return (
    <NavLink
      to={to}
      aria-label={count > 0 ? `${label} (${count})` : label}
      title={label}
      className={({ isActive }) =>
        cn(
          'relative flex flex-1 items-center justify-center rounded-xl border border-border py-2 transition-colors',
          isActive ? 'border-primary/30 bg-primary-soft text-primary' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
        )
      }
    >
      <Icon className="h-4.5 w-4.5" aria-hidden />
      {count > 0 ? (
        <span className="absolute right-2 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
          {count > 9 ? '9+' : count}
        </span>
      ) : null}
    </NavLink>
  );
}

/** Meme raccourci, dans la barre superieure des ecrans etroits. */
function MobileShortcut({
  to,
  label,
  count,
  icon: Icon,
}: {
  to: string;
  label: string;
  count: number;
  icon: typeof Bell;
}) {
  return (
    <Link
      to={to}
      aria-label={count > 0 ? `${label} (${count})` : label}
      className="relative rounded-lg p-2 text-ink transition-colors hover:bg-surface-muted"
    >
      <Icon className="h-5 w-5" aria-hidden />
      {count > 0 ? (
        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
          {count > 9 ? '9+' : count}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * Bandeau d'etat de la connexion.
 *
 * Hors ligne, l'agent continue de consulter les sessions deja ouvertes ; sa
 * progression est mise en file d'attente et rejouee au retour du reseau.
 * Offline, progress is queued and replayed when the network returns.
 */
function ConnectionBanner() {
  const { t } = useLanguage();
  const { online, pending } = useConnection();

  if (online && pending === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-wrap items-center justify-center gap-2 px-4 py-2 text-sm',
        online ? 'bg-warning-soft text-warning' : 'bg-danger-soft text-danger',
      )}
    >
      {online ? <CloudUpload className="h-4 w-4" aria-hidden /> : <CloudOff className="h-4 w-4" aria-hidden />}
      {online ? t('a11y.pendingSync', { count: pending }) : t('a11y.offline')}
    </div>
  );
}
